import * as utils from './utils'
import * as utils_auth from './utils_auth'
import * as utils_cloudinary from './utils_cloudinary'
import { User } from './User'
import { Circle } from './Circle'
import { globalUC } from './UserCache'
import store from './store';

const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

export type howJoinedTypes = 'normal'|'invitedBasic'|'invitedAsTeller'|'invitedAsListener'|'facebookLogin'|'whiteGlove'
export type likelyToBeTypes = 'listener'|'teller'
export type tutorialSteps = 'welcome'|'enterPhoneNumber'|'tutorialStoryCreated'|'recordForTutorial'|'listenToOwnTutorialRecording'|'uploadProfilePic'|'chooseInviteOrExplore'|'inviteListeners'|'inviteStoryTeller'|'poseQuestion'|'finishingScreen';
export type planTypes = 'free'|'paid'
export type emailTypes = 'confirmEmail' | 'passwordReset' | 'invite' | 'commentMade' | 'storyRecordingMade' | 'storyPrompt' | 'circleConfirmation' | 'storyRecordingResult' | 'circleRequest' | 'expirationWarning' | 'expiredStory' | 'automatedStoryPrompt' | 'support' | 'inviteStatus' | 'registrationReminder' | 'poseQuestion'

export type quizSteps = quizMain|quizDemographics|quizFeatures|quizTechnology|quizPersonality|quizInterests|quizMisc|quizInvite|quizRegister
export type quizMain = 'welcome'|'chooseLearningMethod'|'whoIsStoryTeller'|'why'
export type quizDemographics = 'gender'|'age'|'speaksEnglish'|'language'|'unitedStates'|'immigrant'|'children'|'grandchildren'|'numCircle'
export type quizTechnology = 'howCaptureNow'|'technologyKnowledge'|'phoneKind'|'textMessage'|'useEmail'|'whiteGlove'
export type quizFeatures = 'features'|'freeOrPaid'
export type quizPersonality = 'naturallyTalkative'|'senseOfHumour'
export type quizInterests = 'college'|'sports'|'religious'|'pets'
export type quizMisc = 'promptVoice'|'promptFrequency'
export type quizInvite = 'storyTellerName'|'storyTellerEmail'|'storyTellerPhoneNumber'
export type quizRegister = 'register'

export interface quizStepAnswer {
  quizStep: quizSteps,
  completed: boolean,
  skipped: boolean,
  answers: string[]
}

export class StoryUser extends User {
  protected static ALLOW_UNEXPECTED_FIELDS: boolean = false   // cuz we set User to true

  public firstName: string = null
  public lastName: string = null
  public profilePic: string = null
  public phoneNumberIds: string[] = []
  public phoneNumbers: string[] = []
  public likelyToBe: likelyToBeTypes = null
  public tutorialStepsDone: tutorialSteps[] = []
  public tutorialStepsSkipped: tutorialSteps[] = []
  public howJoined: howJoinedTypes = null
  public numRecordings: number = 0
  public tutorialWelcomeVoice: string = null
  public currentlyInCall: boolean = null
  public plan: planTypes = null
  public lastTimeEmailedType: { [ emailType: string ]: number } = {}
  public unsubscribedFromSpecificType: { [ emailType: string ]: boolean } = {}
  public optedOutOfTexts: boolean = null
  public turnedOffAutoPrompter: boolean = null      // NOT EVER SET - CODE USES unsubscribedFromSpecificType[automatedStoryPrompt] right now
  public languages: string[] = []
  public quizStepAnswers: { [ quizStep: string ]: quizStepAnswer } = {}

  public circleId: string = null  // the user's story circle (down the road, if it makes sense, we could make this an array to allow users to own multiple circles (kinda like groups))
  public following: string[] = []
  public pendingOutgoingFollowing: string[] = []
  public automaticallyReciprocateFollows: boolean = null
  public managing: string[] = []

  // we don't need to send these to segment because segment captures these automatically, but we capture these ourselves too for our own DB
  public utm_source: string = null
  public utm_campaign: string = null
  public utm_medium: string = null
  public utm_content: string = null
  public utm_term: string = null

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  // client-only field not meant to be stored in db - utils_mongo.insertOne() and utils_mongo.insertMany() are instrumented to do this
  public _temp: { circle?: Circle } = {}

  constructor(po?: object) {
    super(null)
    if (po) this.injectPlainObject(po)
    this.hydrate()
    if (!this.isSafe()) {
      console.log(`StoryUser ${this.email} not safe!`)
      throw new Error(`StoryUser ${this.email} not safe!`)
    }
  }

  protected hydrate(): void {
    super.hydrate()

    // strange. this single line of code causes the store getter for user and userId to give user
    // "You may have an infinite update loop in a component render function."
    // So I commented it out and make sure code calls getCircle() in realtime
    // if (this._temp && this._temp.circle) this._temp.circle = new Circle(this._temp.circle)
  }

  public isSafe(): boolean {
    if (!super.isSafe()) return false
    return true
  }

  public getFullName(): string {
    return this.firstName + ' ' + this.lastName
  }

  public isFollowing(circleId: string): boolean {
    return this.following.includes(circleId)
  }

  // for client side stuff
  public getHTML(loggedInUserId: string): string {
    const name: string = loggedInUserId === this.getIdAsString() ? 'me' : this.getFullName()
    const url: string = `/user/${this.getIdAsString()}`
    const html: string = `<a href="${url}"><span>${name}</span></a>`
    return html
  }
  public getProfilePicURL(fullsize?: boolean): string {
    if (!this.profilePic) return null
    return utils_cloudinary.getURL(utils_cloudinary.photoType.profilePic, this.profilePic, fullsize)
  }

  // https://segment.com/docs/connections/spec/identify/
  // https://segment.com/docs/guides/how-to-guides/best-practices-identify/
  // on both client and server side
  public getTraitObjectForSegment(): any {
    return {
      // segment reserved traits
      avatar: this.getProfilePicURL(),
      createdAt: this.created.toISOString(),
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      username: this.username,
      phone: this.phoneNumbers[0],
      id: this.getIdAsString(),

      // our own traits
      emailVerified: this.emailVerified,
      howJoined: this.howJoined,
      likelyToBe: this.likelyToBe,
      tutorialWelcomeVoice: this.tutorialWelcomeVoice,
      hasPhoneNumber: this.phoneNumberIds.length > 0,
      numRecordings: this.numRecordings,
      numManaging: this.managing.length,
      numFollowing: this.following.length,
      numPendingOutgoingFollowing: this.pendingOutgoingFollowing.length,
      numTutorialStepsDone: this.tutorialStepsDone.length,
      numTutorialStepsSkipped: this.tutorialStepsSkipped.length,
      invitedBy: this.invitedBy,
      numInvited: this.invited.length,
      lastSeen: (this.lastSeen) ? this.lastSeen.toISOString() : null,
      source: this.source,
      fullyRegistered: this.fullyRegistered,
      plan: this.plan,
      numEmailTypesUnsubscribed: Object.keys(this.unsubscribedFromSpecificType).length,
      automaticallyReciprocateFollows: this.automaticallyReciprocateFollows,
      optedOutOfTexts: this.optedOutOfTexts,
      turnedOffAutoPrompter: this.turnedOffAutoPrompter,
      finishedTutorial: this.tutorialStepsDone.includes('finishingScreen'),
      finishedQuiz: this.finishedQuiz(),
      activityScore: this.activityScore()
    }
  }

  public static getDescriptionFromEmailType(emailType: emailTypes): string {
    if (emailType === 'commentMade') return 'Recent comments on stories'
    if (emailType === 'storyRecordingMade') return 'New recordings from your storytellers'
    if (emailType === 'confirmEmail') return 'Email confirmations'
    if (emailType === 'invite') return 'Invitations to join StoryCircle'
    if (emailType === 'passwordReset') return 'Password reset requests'
    if (emailType === 'storyPrompt') return 'Story prompts'
    if (emailType === 'circleConfirmation') return 'Notifications of circle confirmations'
    if (emailType === 'circleRequest') return 'Requests to join your listening circle'
    if (emailType === 'storyRecordingResult') return 'Confirmation of a successful recording'
    if (emailType === 'expirationWarning') return 'Notice of soon-to-expire stories'
    if (emailType === 'expiredStory') return 'Notice of expired stories'
    if (emailType === 'automatedStoryPrompt') return 'Weekly story prompts'
    if (emailType === 'support') return 'Customer support messages'
    if (emailType === 'inviteStatus') return 'Invitation status messages'
    if (emailType === 'registrationReminder') return 'Registration reminder messages'
    if (emailType === 'poseQuestion') return 'Pose question reminder messages'
    return emailType
  }

  public finishedQuiz(): boolean {
    return this.quizStepAnswers && this.quizStepAnswers['register'] && this.quizStepAnswers['register'].completed === true
  }

  public activityScore(): number {
    return this.numRecordings + this.invited.length
  }

  // tutorial components call these when user completes something or hits next
  public async markTutorialStepDone(step: tutorialSteps): Promise<boolean> {
    console.assert(store.getters.userId === this.getIdAsString())

    if (!this.tutorialStepsDone.includes(step)) {
      this.tutorialStepsDone = this.tutorialStepsDone.concat([ step ])  // we do it this way instead of push to invoke reactivity
      this.updateMeToCacheStoreAndLocalStorage()
    } else {
      return true
    }

    // PUT it to the server
    let url: string = utils.getURLprefix() + "/api/users";
    url += '/' + this.getIdAsString()
    try {
      const response: any = await utils.makePutCall(url, { command: 'markTutorialDone', step });
      let user = response.data.user;
      if (user) {
        user = new StoryUser(user);
        user.updateMeToCacheStoreAndLocalStorage()
        return true
      }
    } catch (error) {
      console.log(`StoryUser.markTutorialStepDone() got ${error}`)
    }
    return false
  }

  // tutorial components call these when user skips
  public async markTutorialStepSkipped(step: tutorialSteps): Promise<boolean> {
    console.assert(store.getters.userId === this.getIdAsString())

    if (!this.tutorialStepsSkipped.includes(step)) {
      this.tutorialStepsSkipped = this.tutorialStepsSkipped.concat([ step ])  // we do it this way instead of push to invoke reactivity
      this.updateMeToCacheStoreAndLocalStorage()
    } else {
      return true
    }

    // PUT it to the server
    let url: string = utils.getURLprefix() + "/api/users";
    url += '/' + this.getIdAsString()
    try {
      const response: any = await utils.makePutCall(url, { command: 'markTutorialSkipped', step });
      let user = response.data.user;
      if (user) {
        user = new StoryUser(user);
        user.updateMeToCacheStoreAndLocalStorage()
        return true
      }
    } catch (error) {
      console.log(`StoryUser.markTutorialStepSkipped() got ${error}`)
    }
    return false
  }

  public updateMeToCacheStoreAndLocalStorage(): void {
    console.assert(this.isSafe())
    globalUC.add(this)
    if (store.getters.userId === this.getIdAsString()) utils_auth.saveAuthStuffToStoreAndLocalStorage(this, null)  // save to store
  }

  public async hasSomeoneToPoseTo(): Promise<boolean> {
    if (!this.following || this.following.length === 0) return false

    const url: string = utils.getURLprefix() + `/api/users/${this.getIdAsString()}/following`
    const response: any = await utils.makeGetCall(url);

    if (response.data.users) {
      const followingUsers: StoryUser[] = response.data.users.map((u) => new StoryUser(u))
      for (const fuser of followingUsers) {
        if (!fuser) continue
        if (fuser.phoneNumberIds && fuser.phoneNumberIds.length > 0) return true
      }
    }
    return false
  }

  public getCircle(): Circle {
    if (this._temp && this._temp.circle) return new Circle(this._temp.circle)
  }

  public isTutorialStepDoneOrSkipped(step: tutorialSteps): boolean {
    return this.tutorialStepsDone.includes(step) || this.tutorialStepsSkipped.includes(step)
  }

  public userDidQuiz(): boolean {
    return this.quizStepAnswers != null
  }

  public userDidQuizAsChampion(): boolean {
    if (this.quizStepAnswers) {
      if (this.quizStepAnswers['whoIsStoryTeller'] && this.quizStepAnswers['whoIsStoryTeller'].answers) {
        if (this.quizStepAnswers['whoIsStoryTeller'].answers.includes('My own')) return false
        return true
      }
    }
    return false
  }

}


(async () => {
})();
